import type { getOneSenderOutput } from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetSenderStatusHistory } from "@/services";
import type { ApiError } from "@/utils/fetching";
import { getTimeSinceText } from "@/utils/timestamp";
import { formatNumber, getAuthFields } from "@/utils/tools";
import {
	type StatusTranslation,
	useSenderStatusTranslator,
} from "@/utils/translations";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Chip,
	type ChipVariant,
	ErrorBoundary,
	Icon,
	Typography,
} from "@st-marketplace/ui";
import type { FC } from "react";
import {
	SenderInfoDetailsSkeleton,
	SenderInfoDetailsStatusHistorySkeleton,
} from "./DetailsSkeleton";

const STATUS_COLOR_TO_CHIP_VARIANT: Record<
	StatusTranslation["color"],
	ChipVariant
> = {
	success: "success",
	primary: "info",
	error: "error",
	warning: "warning",
};

const DetailRow: FC<{ label: string; children: React.ReactNode }> = ({
	label,
	children,
}) => (
	<div className="flex">
		<Typography variant="body-sm" className="w-[35%]">
			{label}
		</Typography>
		<Typography
			variant="body-sm"
			className="w-[65%] truncate text-secondary-text-100"
		>
			{children}
		</Typography>
	</div>
);

const AuthDetails: FC<{
	auth: getOneSenderOutput["auth"];
}> = ({ auth }) => {
	const { t } = useTranslation();
	const fields = getAuthFields(auth, t);

	return (
		<>
			{fields.map((field, index) => (
				<DetailRow key={`${auth.type}-${index}`} label={field.label}>
					{field.render(auth)}
				</DetailRow>
			))}
		</>
	);
};

interface SenderInfoDetailsProps {
	senderId: string;
	data?: getOneSenderOutput;
	loading: boolean;
	error: ApiError | Error | null;
	refetch: () => void;
}

const SenderInfoDetails: FC<SenderInfoDetailsProps> = ({
	senderId,
	data,
	loading,
	error,
	refetch,
}) => {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();
	const translateStatus = useSenderStatusTranslator(t);

	const {
		data: statusHistories,
		isLoading,
		error: getSenderStatusHistoryError,
		refetch: statusRefetch,
	} = useGetSenderStatusHistory({ path: { sender_id: senderId } });

	const statusError = getSenderStatusHistoryError
		? getErrorInterface(getSenderStatusHistoryError)
		: null;

	const details: { id: number; label: string; value: any }[] = [
		{ id: 1, label: t("senderInfo.info.cardInfoEmail"), value: data?.ea_email },
		{
			id: 2,
			label: t("senderInfo.info.cardInfoPassword"),
			value: data?.ea_password,
		},
		{
			id: 3,
			label: t("senderInfo.info.cardInfoGroupName"),
			value: data?.group_name,
		},
		{
			id: 4,
			label: t("senderInfo.info.cardInfoReservedBalance"),
			value: formatNumber(data?.resolved_to_keep.value || 0, "count"),
		},
		{
			id: 5,
			label: t("senderInfo.info.cardInfoSpendableBalance"),
			value: formatNumber(data?.to_use || 0, "count"),
		},
		{
			id: 6,
			label: t("senderInfo.info.cardInfoProxyAddress"),
			value: data?.proxy?.address,
		},
		{
			id: 7,
			label: t("senderInfo.info.cardInfoProxyPort"),
			value: data?.proxy?.port,
		},
	];

	return (
		<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
			<ErrorBoundary
				isLoading={loading}
				error={error ?? undefined}
				onReset={refetch}
				skeleton={<SenderInfoDetailsSkeleton />}
			>
				<Card>
					<CardHeader>
						<div className="flex items-center gap-3">
							<Icon name="information-line" className="text-primary-100" />
							<CardTitle>
								<Typography variant="title-sm">
									{t("senderInfo.info.cardInfoTitle").toUpperCase()}
								</Typography>
							</CardTitle>
						</div>
					</CardHeader>
					<CardContent className="ms-0 flex flex-col gap-5 lg:ms-9">
						{data && (
							<>
								{details.map(({ id, label, value }) => (
									<DetailRow key={id} label={label}>
										{value}
									</DetailRow>
								))}

								<AuthDetails auth={data.auth} />
							</>
						)}
					</CardContent>
				</Card>
			</ErrorBoundary>

			<ErrorBoundary
				isLoading={isLoading}
				error={statusError ?? undefined}
				onReset={statusRefetch}
				skeleton={<SenderInfoDetailsStatusHistorySkeleton />}
			>
				<Card className="h-full">
					<CardHeader>
						<div className="flex items-center gap-3">
							<Icon name="checkbox-circle-line" className="text-primary-100" />
							<CardTitle>
								<Typography variant="title-sm">
									{t("senderInfo.info.cardStatusHistoryTitle").toUpperCase()}
								</Typography>
							</CardTitle>
						</div>
					</CardHeader>
					<CardContent className="flex max-h-105 flex-col gap-5 overflow-y-auto">
						{statusHistories && (
							<>
								{[...statusHistories.statuses].reverse().map((item, index) => {
									const { color, text } = translateStatus(item.status);
									return (
										<div
											key={`${item.status}-${index}`}
											className="flex items-center justify-between"
										>
											<Chip
												variant={STATUS_COLOR_TO_CHIP_VARIANT[color]}
												size="sm"
											>
												{text}
											</Chip>
											<Typography variant="body-sm">
												{getTimeSinceText(item.timestamp, t, lang)}
											</Typography>
										</div>
									);
								})}
							</>
						)}
					</CardContent>
				</Card>
			</ErrorBoundary>
		</div>
	);
};

export default SenderInfoDetails;
